import { LAST_NOTIFICATION_DATE_FIELD } from 'components/OrderSlidesChart/helpers/constants';
import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { MiniDrawerWithContext } from 'components/Layout/drawer';
import { useTitle } from 'components/utilities/hooks/useTitle';
import { TeamSelectionCard } from 'components/TeamOverview/components/TeamSelection/TeamSelectionCard';
import { useLocation } from 'react-router';
import { useGetTeamDetailsQuery, useGetTeamsQuery } from 'store/apis/teamsApi';
import { CardsMenuSection } from 'components/TeamOverview/CardsMenuSection/CardsMenuSection';
import {
  BILLING_SECTION,
  COMPUTE_SECTION,
  DATA_MANAGEMENT_SECTION,
  ORDER_HISTORY_SECTION,
  PRICE_ROUNDING_COEFFICIENT,
  SlideType,
  TEAM_MEMBERS_SECTION,
  TEAM_OVERVIEW_SECTIONS,
} from 'components/TeamOverview/constants';
import { DataManagementSection } from 'components/TeamOverview/DataManagementSection/DataManagementSection';
import { OrderHistorySection } from 'components/TeamOverview/OrderHistorySection/OrderHistorySection';
import { TeamSection } from 'components/TeamOverview/TeamSection/TeamSection';
import { ComputeSection } from 'components/TeamOverview/ComputeSection/ComputeSection';
import { useDispatch, useSelector } from 'react-redux';
import {
  useGetUserDetailsQuery,
  useUpdateUserPreferencesMutation,
} from 'store/apis/userDetailsApi';
import {
  currentUserPreferencesSelector,
  selectForceOpenTeamOverviewModal,
  setForceOpenTeamOverviewModal,
} from 'store/slices/userDetailsSlice';
import { getTabValueByHash } from 'utilities/general';
import { useTabsNavigation } from 'utilities/hooks/useTabsNavigation/useTabsNavigation';
import {
  LAST_NOTIFICATION_UPDATE_DATE,
  NotificationsModal,
} from 'components/TeamOverview/components/NotificationsModal/NotificationsModal';
import { BillingSection } from 'components/TeamOverview/BillingSection/BillingSection';

export const TeamOverviewV2 = () => {
  useTitle('HistoWiz - Team Overview');
  const showChangesModal = useSelector(selectForceOpenTeamOverviewModal);
  const lastNotificationCheckDate = useSelector(currentUserPreferencesSelector)[
    LAST_NOTIFICATION_DATE_FIELD
  ];
  const { isLoading: isUserDetailsLoading } = useGetUserDetailsQuery();
  const dispatch = useDispatch();
  const location = useLocation();
  const [updateUserPreferences] = useUpdateUserPreferencesMutation();
  const [changesModalOpen, setChangesModalOpen] = useState(false);
  const [selectedTeamUUID, setSelectedTeamUUID] = useState(null);
  const [slideType, setSlideType] = useState(SlideType.IHC);
  const sectionFromHash = getTabValueByHash(
    location.hash,
    TEAM_OVERVIEW_SECTIONS,
    ORDER_HISTORY_SECTION,
  );
  const [selectedSection, setSelectedSection] = useState(sectionFromHash);
  const isTeamMembersActive = selectedSection === TEAM_MEMBERS_SECTION;
  const { data: teams, isLoading: isTeamsLoading } = useGetTeamsQuery();
  const { data: detailedTeamInformation, isLoading: isTeamLoading } =
    useGetTeamDetailsQuery(
      { teamUUID: selectedTeamUUID },
      { skip: !selectedTeamUUID },
    );

  useEffect(() => {
    setSelectedSection(sectionFromHash);
  }, [location.hash]);

  useTabsNavigation(selectedSection);

  useEffect(() => {
    if (!teams?.length) return;

    if (!selectedTeamUUID) {
      setSelectedTeamUUID(teams[0].uuid);
    }
  }, [teams]);

  useEffect(() => {
    if (isUserDetailsLoading) return;

    if (
      !lastNotificationCheckDate ||
      lastNotificationCheckDate < LAST_NOTIFICATION_UPDATE_DATE
    ) {
      setChangesModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastNotificationCheckDate, isUserDetailsLoading]);

  const handleCloseChangesModal = () => {
    setChangesModalOpen(false);
    dispatch(setForceOpenTeamOverviewModal(false));
    const postParams = {
      [LAST_NOTIFICATION_DATE_FIELD]: new Date().getTime(),
    };

    updateUserPreferences({
      postParams,
      partial: true,
    });
  };

  const headerLabel = `${detailedTeamInformation?.name || 'Team'} Overview`;

  const dataUsage = detailedTeamInformation?.total_storage_used
    ? +(detailedTeamInformation.total_storage_used / 1000 ** 2).toFixed(2)
    : 0;

  const price =
    dataUsage < 0.05 ? '0' : Math.ceil(dataUsage) * PRICE_ROUNDING_COEFFICIENT;

  const SECTIONS = {
    [ORDER_HISTORY_SECTION]: (
      <OrderHistorySection selectedTeam={detailedTeamInformation} />
    ),
    [DATA_MANAGEMENT_SECTION]: (
      <DataManagementSection
        isTeamsLoading={isTeamsLoading}
        team={detailedTeamInformation}
        slideType={slideType}
        setSlideType={setSlideType}
      />
    ),
    [BILLING_SECTION]: <BillingSection team={detailedTeamInformation} />,
    [COMPUTE_SECTION]: <ComputeSection team={detailedTeamInformation} />,
    [TEAM_MEMBERS_SECTION]: (
      <TeamSection
        team={detailedTeamInformation}
        isTeamLoading={isTeamLoading || isTeamsLoading}
      />
    ),
  };

  return (
    <MiniDrawerWithContext header={headerLabel}>
      <Grid item container xs={12} rowSpacing={2}>
        <TeamSelectionCard
          selectedTeam={detailedTeamInformation}
          setSelectedTeamUUID={setSelectedTeamUUID}
          teams={teams}
          isTeamMembersActive={isTeamMembersActive}
        />
        {!isTeamMembersActive && (
          <CardsMenuSection
            selectedSection={selectedSection}
            setSelectedSection={setSelectedSection}
            selectedTeam={detailedTeamInformation}
          />
        )}
        <Grid item container>
          {SECTIONS[selectedSection]}
        </Grid>
      </Grid>
      {detailedTeamInformation && (
        <NotificationsModal
          open={changesModalOpen}
          onClose={handleCloseChangesModal}
          dataUsage={dataUsage}
          price={price}
          teamName={detailedTeamInformation?.name}
          forceOpen={showChangesModal}
        />
      )}
    </MiniDrawerWithContext>
  );
};
